/* ============================================================
   Laley PDP Clean v4 — variants
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: variants');
    return;
  }
  const { cssEscape } = PDP.utils;

  PDP.registerModule('variants', {
    cleanVariantLabel(variant) {
      const title = String(variant?.title || '').trim();
      if (!title || /^default title$/i.test(title)) return '';
      return title;
    },

    optionGroupDisplayName(group) {
      const triggerName = group?.querySelector?.('.pdp-clean__dropdown-trigger-name')?.textContent?.trim();
      if (triggerName) return triggerName;
      const legendText = group?.querySelector?.('legend')?.textContent || '';
      const name = legendText.split(':')[0].replace(/\s+/g, ' ').trim();
      return name || 'delen';
    },

    variantFromInput() {
      const id = this.variantInput?.value || this.section.querySelector('[data-pdp-variant-id]')?.value;
      return this.variants.find((variant) => String(variant.id) === String(id));
    },

    variantFromUrl() {
      const id = new URLSearchParams(window.location.search).get('variant');
      if (!id) return null;
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    },

    hasRequiredSizeSelection() {
      if (!this.requiresSizeSelection) return true;
      const groups = this.optionGroups.filter((group) => group.hasAttribute('data-pdp-size-group'));
      if (!groups.length) return true;
      return groups.every((group) => Boolean(group.querySelector('[data-pdp-option-input]:checked')));
    },

    selectedOptions() {
      if (!this.optionGroups.length) return this.currentVariant?.options || [];
      // Index by the input's real Shopify option position, NOT picker DOM order.
      // The template renders colour before size for visual reasons, but Shopify's
      // option order can be the reverse (e.g. variant title "XS / Black" → size is
      // option 1). findVariant/updateSelectedLabels/updateOptionStates all read this
      // back by data-option-index, so it must be index-aligned or the values swap.
      const opts = [];
      this.optionGroups.forEach((group) => {
        const checked = group.querySelector('[data-pdp-option-input]:checked');
        if (!checked) return;
        const idx = Number(checked.dataset.optionIndex);
        opts[idx] = checked.value;
      });
      return opts;
    },

    findVariant(options = this.selectedOptions()) {
      if (!options.length) return this.variants[0] || null;
      return this.variants.find((variant) => {
        return variant.options.length === options.length && variant.options.every((option, index) => option === options[index]);
      }) || null;
    },

    syncOptionInputsToVariant(variant) {
      if (!variant?.options?.length) return;
      variant.options.forEach((value, index) => {
        const input = this.section.querySelector(`[data-pdp-option-input][data-option-index="${index}"][value="${cssEscape(value)}"]`);
        if (input && !input.checked) input.checked = true;
      });
    },

    bindOptions() {
      this.optionInputs.forEach((input) => {
        this.on(input, 'change', () => {
          // CP-stil affordance: ATC sier "Velg din størrelse" inntil bruker
          // har trigget minst én option-change. Etter første klikk: vis pris.
          this.userPicked = true;
          const variant = this.findVariant();
          this.renderVariant(variant, { updateUrl: true, syncGallery: true, behavior: 'smooth' });
          this.updateOptionStates();
          // Lukk dropdown etter valg + oppdater trigger-verdi
          const dropdown = input.closest('[data-pdp-dropdown]');
          if (dropdown) {
            dropdown.open = false;
            const trigger = dropdown.querySelector('[data-pdp-dropdown-value]');
            if (trigger) trigger.textContent = input.value;
          }
        });
      });
    },

    updateOptionStates() {
      if (!this.optionInputs.length) return;
      const selected = this.selectedOptions();
      const bundleSizeIndexes = this.bundleSizeOptionIndexes();

      this.optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const candidate = selected.slice();
        candidate[optionIndex] = input.value;
        const isBundleSizeOption = bundleSizeIndexes.size > 1 && bundleSizeIndexes.has(optionIndex);

        const matches = this.variants.filter((variant) => {
          return variant.options.every((value, index) => {
            if (index === optionIndex) return value === input.value;
            // Bundle-deler skal ikke arve "utsolgt" fra en annen valgt del.
            // Eks: genser XL finnes selv om shorts XL er utsolgt.
            if (isBundleSizeOption && bundleSizeIndexes.has(index)) return true;
            return !candidate[index] || value === candidate[index];
          });
        });

        const exists = matches.length > 0;
        const anyAvailable = matches.some((variant) => variant.available);
        const label = this.section.querySelector(`label[for="${cssEscape(input.id)}"]`);

        input.disabled = !exists;
        if (label) label.dataset.soldout = exists && !anyAvailable ? 'true' : 'false';
      });
      this.updateBundleSharedState();
    },

    renderVariant(variant, options = {}) {
      this.currentVariant = variant || null;
      this.clearError();

      if (this.variantInput && variant) {
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updateSelectedLabels();
      this.updatePrice(variant);
      this.updateButtons(variant);
      this.updateVariantStatus(variant);
      this.updateBis(variant);
      this.updateBundle(variant);
      this.updateStickyContent(variant);
      this.updateSizePillsStock();

      if (options.syncGallery && variant) this.syncGalleryToVariant(variant, options.behavior || 'smooth');
      if (options.updateUrl && variant) this.updateUrl(variant);

      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant,
          productId: this.section.dataset.productId,
          sectionId: this.section.dataset.sectionId,
        },
      }));
    },

    updateSelectedLabels() {
      const selected = this.selectedOptions();
      this.section.querySelectorAll('[data-pdp-selected-option]').forEach((label) => {
        const index = Number(label.dataset.optionIndex);
        if (selected[index]) {
          label.textContent = this.localizeColor(selected[index]);
          return;
        }
        const group = this.optionGroups.find((item) => Number(item.dataset.optionIndex) === index);
        const isColor = Boolean(group?.querySelector('.pdp-clean__option--swatch'));
        label.textContent = isColor ? '' : 'Velg';
      });
    },

    // Norsk VISNINGSNAVN for fargeverdier. Dictionary kommer fra theme-setting
    // (data-pdp-color-map = "Black:Svart, ..."), så det er en redigerbar kilde, ikke
    // hardkodet. Variantverdien beholdes uendret (f.eks. "Black"); kun visning byttes.
    // Ukjente verdier (størrelser som XS) returneres uendret.
    localizeColor(value) {
      if (!this._colorMap) {
        this._colorMap = {};
        (this.section.dataset.pdpColorMap || '').split(',').forEach((pair) => {
          const i = pair.indexOf(':');
          if (i === -1) return;
          const k = pair.slice(0, i).trim().toLowerCase().replace(/\s+/g, '');
          const v = pair.slice(i + 1).trim();
          if (k && v) this._colorMap[k] = v;
        });
      }
      const key = String(value).toLowerCase().replace(/\s+/g, '');
      return this._colorMap[key] || value;
    },

    updatePrice(variant) {
      if (!variant) return;
      if (this.priceCurrent) this.priceCurrent.textContent = this.money(variant.price);
      if (this.priceCompare) {
        this.priceCompare.textContent = variant.compareAtPrice ? this.money(variant.compareAtPrice) : '';
        this.priceCompare.hidden = !variant.isOnSale;
      }
      if (this.priceSaving) {
        const hasSaving = variant.isOnSale && Number(variant.savingPercent) > 0;
        this.priceSaving.textContent = hasSaving ? `-${variant.savingPercent}% — tilbudspris` : '';
        this.priceSaving.hidden = !hasSaving;
      }
      const priceWrap = this.section.querySelector('[data-pdp-price-wrap]');
      if (priceWrap) priceWrap.classList.toggle('is-on-sale', !!variant.isOnSale);
    },

    updateButtons(variant) {
      if (!this.atc) return;
      // Ingen falsk add-to-cart før kunden har valgt størrelse. Pick-mode
      // scroll/fokuserer size-pickeren og viser inline-feil ved klikk.
      const needsSize = !this.hasRequiredSizeSelection();

      if (needsSize) {
        this.atc.disabled = false;
        this.atc.dataset.mode = 'pick';
        this.atc.textContent = 'Velg størrelse';
        return;
      }
      if (!variant) {
        this.atc.disabled = true;
        this.atc.dataset.mode = 'unavailable';
        this.atc.textContent = 'Velg alternativer';
        return;
      }
      if (!variant.available) {
        this.atc.disabled = false;
        this.atc.dataset.mode = 'bis';
        this.atc.textContent = 'Varsle meg';
        return;
      }
      this.atc.disabled = false;
      this.atc.dataset.mode = 'add';
      this.atc.textContent = 'Legg i handlekurv — ';
      const price = document.createElement('span');
      price.setAttribute('data-pdp-atc-price', '');
      price.textContent = this.money(variant.price);
      this.atc.appendChild(price);
    },

    updateSizePillsStock() {
      // CP-stil: vis differensiert melding per size: "Kun X igjen", "Få igjen",
      // "Snart utsolgt", "Utsolgt". Kjører på alle ikke-color grupper (inkl.
      // bundle-komponenter som "Flared Yoga Tights").
      const stockLabel = (variant) => {
        if (!variant || !variant.available) return { text: 'Utsolgt', cls: 'is-out' };
        // Lagersignalet er bucketet server-side (stockState/lowCount) — eksakte
        // tall finnes ikke lenger i klienten utover det som vises (1 og 2).
        if (variant.stockState !== 'low') return null;
        const lowCount = Number(variant.lowCount);
        if (lowCount === 1) return { text: 'Kun 1 igjen', cls: 'is-low' };
        if (lowCount === 2) return { text: 'Kun 2 igjen', cls: 'is-low' };
        return { text: 'Få igjen', cls: 'is-low' };
      };

      // Iterer over alle "size-like" grupper (alle ikke-color picker-groups),
      // både dropdown og inline pills. Inline pills trenger like tydelig lagerstatus.
      const sizeGroups = this.optionGroups.filter((g) => !g.querySelector('.pdp-clean__option--swatch'));
      const bundleSizeIndexes = this.bundleSizeOptionIndexes();
      sizeGroups.forEach((sizeGroup) => {
        const sizeOptionIndex = Number(sizeGroup.dataset.optionIndex);
        const isBundleSizeGroup = bundleSizeIndexes.size > 1 && bundleSizeIndexes.has(sizeOptionIndex);
        const otherOptionValues = this.optionGroups
          .filter((g) => g !== sizeGroup)
          .map((g) => ({
            index: Number(g.dataset.optionIndex),
            value: g.querySelector('[data-pdp-option-input]:checked')?.value || null,
          }))
          .filter((other) => !(isBundleSizeGroup && bundleSizeIndexes.has(other.index)));

        sizeGroup.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
          const sizeValue = input.value;
          const matches = this.variants.filter((v) => {
            if (v.options[sizeOptionIndex] !== sizeValue) return false;
            return otherOptionValues.every((other) => {
              if (other.value === null) return true;
              return v.options[other.index] === other.value;
            });
          });
          const variant = matches.find((v) => v.available) || matches[0] || null;
          const label = input.nextElementSibling;
          const stockEl = label?.querySelector('[data-pdp-option-stock]');
          if (!stockEl) return;
          stockEl.classList.remove('is-low', 'is-soon', 'is-out');
          const info = stockLabel(variant);
          if (info) {
            stockEl.textContent = info.text;
            stockEl.classList.add(info.cls);
            stockEl.hidden = false;
          } else {
            stockEl.hidden = true;
            stockEl.textContent = '';
          }
        });
      });
    },

    updateVariantStatus(variant) {
      // Kontekstuell beskjed under størrelser. Lavt lager vises på selve
      // størrelsesknappen; denne linjen brukes kun når kunden velger utsolgt.
      const slot = this.section.querySelector('[data-pdp-variant-status]');
      const text = slot?.querySelector('[data-pdp-variant-status-text]');
      if (!slot || !text || !variant) return;
      slot.classList.remove('is-soldout', 'is-low');
      if (!variant.available) {
        const label = this.cleanVariantLabel(variant);
        const sharedPill = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden]) .pdp-clean__bundle-shared-pill.is-selected[data-soldout="true"]');
        if (sharedPill) {
          slot.removeAttribute('aria-label');
          slot.hidden = true;
          text.replaceChildren();
          return;
        }
        const bundleStatus = this.bundleMixedStatusCopy(variant);
        const title = document.createElement('strong');
        const copy = document.createElement('span');
        title.textContent = bundleStatus?.title || (label ? `${label} er utsolgt` : 'Utsolgt');
        copy.textContent = bundleStatus?.copy || 'Få varsel når den er tilbake';
        text.replaceChildren(title, copy);
        slot.setAttribute('aria-label', [title.textContent, copy.textContent].filter(Boolean).join('. '));
        slot.classList.add('is-soldout');
        slot.hidden = false;
      } else {
        slot.removeAttribute('aria-label');
        slot.hidden = true;
      }
    },

    updateBis(variant) {
      // State-maskin for sold-out/Varsle-meg-flow.
      // Klaviyo injekter <a class="klaviyo-bis-trigger"> direkte i form når
      // den vurderer current variant som sold-out. Vi:
      //   1. Setter data-variant-available på root for CSS-targeting
      //   2. Flytter Klaviyo-trigger inn i vår [data-pdp-bis]-slot
      //   3. Skjuler trigger når variant er available
      const isAvailable = Boolean(variant && variant.available);
      this.section.dataset.variantAvailable = isAvailable ? 'true' : 'false';

      const injectedTrigger = this.form?.querySelector('.klaviyo-bis-trigger')
        || this.section.querySelector('.klaviyo-bis-trigger');

      if (this.bis && injectedTrigger && !this.bis.contains(injectedTrigger)) {
        this.bis.appendChild(injectedTrigger);
      }

      const hasTriggerInSlot = Boolean(this.bis?.querySelector('.klaviyo-bis-trigger'));

      if (isAvailable) {
        if (this.bis) this.bis.hidden = true;
        if (injectedTrigger) injectedTrigger.hidden = true;
        return;
      }
      // Sold-out: vis BIS-slot hvis Klaviyo har plassert trigger
      if (this.bis) this.bis.hidden = !hasTriggerInSlot;
      if (injectedTrigger) injectedTrigger.hidden = false;
    },

    bindBisObserver() {
      // Klaviyo injekter trigger asynkront — observer både section + form
      // (form refereres ikke alltid før Klaviyo hydrerer).
      if (!this.section) return;
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          this.updateBis(this.currentVariant);
        });
      };
      const observer = new MutationObserver(schedule);
      observer.observe(this.section, { childList: true, subtree: true });
      this.unsubs.push(() => {
        if (raf) window.cancelAnimationFrame(raf);
        observer.disconnect();
      });
    },

    updateUrl(variant) {
      if (!window.history?.replaceState || !variant?.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    },
  });
})();
